import React, { useEffect, useState } from 'react';
import {
    AutocompleteInput,
    DateTimeInput,
    FormDataConsumer,
    NumberInput,
    ReferenceInput,
    SaveButton,
    SelectInput,
    SimpleForm,
    TextInput,
    Toolbar,
    required,
    useDataProvider,
    useRedirect,
} from 'react-admin';
import { Button, Grid, Typography } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';

const toNumberOrZero = (value: any) => {
    if (value === null || value === undefined || value === '') return 0;
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : 0;
};

const ORDER_GST_RATE = 0.18;
const ORDER_DISCOUNT_TYPES = [
    { id: 'percent', name: 'Percent (%)' },
    { id: 'fixed', name: 'Fixed per kg' },
];

const toRate = (value: any, fallback = ORDER_GST_RATE) => {
    if (value === null || value === undefined || value === '') return fallback;
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return fallback;
    if (numericValue > 1) return numericValue / 100;
    return numericValue;
};

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const calculateOrderAmounts = (data: any) => {
    const quantity = toNumberOrZero(data.order_quantity);
    const price = toNumberOrZero(data.order_price);
    const commissionPerKg = toNumberOrZero(data.order_commission);
    const deliveryCharges = toNumberOrZero(data.order_delivery_charges);
    const commercialCreditNotePerKg = toNumberOrZero(data.order_commercial_credit_note_discount);
    const gstRate = clamp(toRate(data.order_gst_rate, ORDER_GST_RATE), 0, 1);
    const discountType = data.order_discount_type === 'fixed' ? 'fixed' : 'percent';
    const discountValue = toNumberOrZero(data.order_discount_value);

    const totalKg = Number((quantity * 1000).toFixed(2));
    const materialAmount = Number((quantity * price * 1000).toFixed(2));
    const commissionAmount = Number((commissionPerKg * totalKg).toFixed(2));
    const subtotalBeforeDiscount = Number((materialAmount + commissionAmount + deliveryCharges).toFixed(2));
    const rawDiscount =
        discountType === 'fixed'
            ? Number((discountValue * totalKg).toFixed(2))
            : Number(((subtotalBeforeDiscount * clamp(discountValue, 0, 100)) / 100).toFixed(2));
    const discountAmount = Number(clamp(rawDiscount, 0, subtotalBeforeDiscount).toFixed(2));
    const taxableAmount = Number(Math.max(0, subtotalBeforeDiscount - discountAmount).toFixed(2));
    const gstAmount = Number((taxableAmount * gstRate).toFixed(2));
    const commercialCreditNoteAmount = Number((commercialCreditNotePerKg * totalKg).toFixed(2));
    const finalTotal = Number(Math.max(0, taxableAmount + gstAmount - commercialCreditNoteAmount).toFixed(2));

    return {
        gstRate,
        discountType,
        discountValue,
        quantity,
        price,
        totalKg,
        baseAmount: materialAmount,
        commissionPerKg,
        commissionAmount,
        deliveryCharges,
        subtotalBeforeDiscount,
        gstAmount,
        discountAmount,
        commercialCreditNotePerKg,
        commercialCreditNoteAmount,
        finalTotal,
    };
};

const getDefaultOrderDate = () => {
    const date = new Date();
    date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
    return date.toISOString().slice(0, 16);
};

export const transformOrder = (data: any) => ({
    ...(function calculated() {
        const amounts = calculateOrderAmounts(data);
        return {
            order_gst_rate: amounts.gstRate,
            order_discount_type: amounts.discountType,
            order_discount_value: amounts.discountValue,
            order_base_total: amounts.baseAmount,
            order_gst_amount: amounts.gstAmount,
            order_discount_amount: amounts.discountAmount,
            order_commercial_credit_note_discount: amounts.commercialCreditNotePerKg,
            order_commercial_credit_note_amount: amounts.commercialCreditNoteAmount,
            order_total: amounts.finalTotal,
        };
    })(),
    ...data,
    order_date: data.order_date || new Date().toISOString(),
    order_customer_id: data.order_customer_id,
    order_product_id: data.order_product_id,
    order_quantity: toNumberOrZero(data.order_quantity),
    order_price: toNumberOrZero(data.order_price),
    order_commission: toNumberOrZero(data.order_commission),
    order_delivery_charges: toNumberOrZero(data.order_delivery_charges),
    order_commercial_credit_note_discount: toNumberOrZero(data.order_commercial_credit_note_discount),
    order_status_id: data.order_status_id || 1,
    order_tracking_url: data.order_tracking_url || '',
    order_state_code: data.order_state_code || null,
    order_city_id: toNumberOrZero(data.order_city_id),
});

const formatCustomerChoice = (record: any) => {
    if (!record) return '';
    const name = [record.customer_first_name, record.customer_last_name].filter(Boolean).join(' ');
    const company = record.company_name ? ` - ${record.company_name}` : '';
    const phone = record.phone_mobile || record.customer_username || record.id;
    return `${phone}${name ? ` - ${name}` : ''}${company}`;
};

const formatProductChoice = (record: any) => {
    if (!record) return '';
    const grade = record.product_grade ? ` - ${record.product_grade}` : '';
    const mfi = record.product_mfi ? `, ${record.product_mfi} MFI` : '';
    return `${record.product_name || record.id}${grade}${mfi}`;
};

const CalculatedTotalInput = () => {
    const { control, setValue } = useFormContext();
    const quantity = useWatch({ control, name: 'order_quantity' });
    const price = useWatch({ control, name: 'order_price' });
    const gstRate = useWatch({ control, name: 'order_gst_rate' });
    const discountType = useWatch({ control, name: 'order_discount_type' });
    const discountValue = useWatch({ control, name: 'order_discount_value' });
    const commission = useWatch({ control, name: 'order_commission' });
    const deliveryCharges = useWatch({ control, name: 'order_delivery_charges' });
    const commercialCreditNoteDiscount = useWatch({ control, name: 'order_commercial_credit_note_discount' });
    const amounts = calculateOrderAmounts({
        order_quantity: quantity,
        order_price: price,
        order_gst_rate: gstRate,
        order_discount_type: discountType,
        order_discount_value: discountValue,
        order_commission: commission,
        order_delivery_charges: deliveryCharges,
        order_commercial_credit_note_discount: commercialCreditNoteDiscount,
    });

    useEffect(() => {
        setValue('order_gst_rate', amounts.gstRate, { shouldDirty: true, shouldValidate: false });
        setValue('order_discount_type', amounts.discountType, { shouldDirty: true, shouldValidate: false });
        setValue('order_discount_value', amounts.discountValue, { shouldDirty: true, shouldValidate: false });
        setValue('order_base_total', amounts.baseAmount, { shouldDirty: true, shouldValidate: false });
        setValue('order_gst_amount', amounts.gstAmount, { shouldDirty: true, shouldValidate: false });
        setValue('order_discount_amount', amounts.discountAmount, { shouldDirty: true, shouldValidate: false });
        setValue('order_commercial_credit_note_discount', amounts.commercialCreditNotePerKg, { shouldDirty: true, shouldValidate: false });
        setValue('order_commercial_credit_note_amount', amounts.commercialCreditNoteAmount, { shouldDirty: true, shouldValidate: false });
        setValue('order_total', amounts.finalTotal, { shouldDirty: true, shouldValidate: false });
    }, [amounts, setValue]);

    const discountDetail =
        discountType === 'fixed'
            ? ` = ${toNumberOrZero(discountValue).toFixed(2)}/kg x ${amounts.quantity.toFixed(2)} x 1000`
            : '';
    const breakdownText = `Total ${amounts.finalTotal.toFixed(2)} (Material ${amounts.baseAmount.toFixed(2)} = Quantity ${amounts.quantity.toFixed(2)} x Price ${amounts.price.toFixed(2)} x 1000; Commission ${amounts.commissionAmount.toFixed(2)} = ${amounts.commissionPerKg.toFixed(2)}/kg x ${amounts.quantity.toFixed(2)} x 1000; Delivery Charges ${amounts.deliveryCharges.toFixed(2)}; Discount ${amounts.discountAmount.toFixed(2)}${discountDetail}; GST ${amounts.gstAmount.toFixed(2)}; Commercial Credit Note ${amounts.commercialCreditNoteAmount.toFixed(2)} = ${amounts.commercialCreditNotePerKg.toFixed(2)}/kg x ${amounts.quantity.toFixed(2)} x 1000)`;

    useEffect(() => {
        setValue('order_total_formula_display', breakdownText, { shouldDirty: false, shouldValidate: false });
    }, [breakdownText, setValue]);

    return (
        <>
            <TextInput
                source="order_total_formula_display"
                label="Calculated Total"
                disabled
                fullWidth
                multiline
                helperText="Formula: (Qty x Price x 1000) + (Commission/kg x Qty x 1000) + Delivery - Discount + GST - (CCN/kg x Qty x 1000). Percent discount on subtotal; fixed discount is per kg."
            />
        </>
    );
};

const OrderLocationInputs = () => {
    const dataProvider = useDataProvider();
    const { control } = useFormContext();
    const selectedStateCode = useWatch({ control, name: 'order_state_code' });
    const [states, setStates] = useState<any[]>([]);
    const [cities, setCities] = useState<any[]>([]);

    useEffect(() => {
        dataProvider
            .getList('states', {
                pagination: { page: 1, perPage: 200 },
                sort: { field: 'state_name', order: 'ASC' },
                filter: {},
            })
            .then(({ data }) => setStates(data))
            .catch(() => setStates([]));
    }, [dataProvider]);

    useEffect(() => {
        const selectedState = states.find((state) => state.state_code === selectedStateCode);
        if (!selectedState) {
            setCities([{ id: 0, city_name: 'Ex-Godown' }]);
            return;
        }

        dataProvider
            .getList('statecities', {
                pagination: { page: 1, perPage: 500 },
                sort: { field: 'city_name', order: 'ASC' },
                filter: { state_id: selectedState.id },
            })
            .then(({ data }) => setCities([{ id: 0, city_name: 'Ex-Godown' }, ...data]))
            .catch(() => setCities([{ id: 0, city_name: 'Ex-Godown' }]));
    }, [dataProvider, selectedStateCode, states]);

    return (
        <>
            <Grid item xs={12} sm={6}>
                <SelectInput
                    source="order_state_code"
                    label="State"
                    choices={states}
                    optionText="state_name"
                    optionValue="state_code"
                    fullWidth
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <SelectInput
                    source="order_city_id"
                    label="City"
                    choices={cities}
                    optionText="city_name"
                    optionValue="id"
                    fullWidth
                />
            </Grid>
        </>
    );
};

interface OrderFormProps {
    toolbar?: React.ReactElement;
}

export const OrderFormToolbar = (props: any) => {
    const redirect = useRedirect();

    return (
        <Toolbar {...props}>
            <SaveButton />
            <Button color="primary" onClick={() => redirect('/orders')} sx={{ ml: 1 }}>
                Back
            </Button>
        </Toolbar>
    );
};

export const OrderForm = ({ toolbar }: OrderFormProps) => (
    <SimpleForm
        toolbar={toolbar || <OrderFormToolbar />}
        defaultValues={{
            order_status_id: 1,
            order_date: getDefaultOrderDate(),
            order_gst_rate: ORDER_GST_RATE,
            order_discount_type: 'percent',
            order_discount_value: 0,
        }}
        sanitizeEmptyValues
    >
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Typography variant="h6">Order Details</Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
                <DateTimeInput source="order_date" label="Order Date" validate={[required()]} fullWidth />
            </Grid>
            <Grid item xs={12} sm={6}>
                <ReferenceInput source="order_status_id" reference="orders/getStatus" label="Order Status">
                    <SelectInput optionText="order_status" validate={[required()]} fullWidth />
                </ReferenceInput>
            </Grid>

            <OrderLocationInputs />

            <Grid item xs={12} sm={6}>
                <ReferenceInput
                    source="order_customer_id"
                    reference="customers"
                    label="Customer"
                    sort={{ field: 'customer_username', order: 'ASC' }}
                >
                    <AutocompleteInput
                        optionText={formatCustomerChoice}
                        filterToQuery={(searchText: string) => ({ customer_search: searchText })}
                        validate={[required()]}
                        fullWidth
                    />
                </ReferenceInput>
            </Grid>
            <Grid item xs={12} sm={6}>
                <ReferenceInput
                    source="order_product_id"
                    reference="products"
                    label="Product"
                    sort={{ field: 'product_name', order: 'ASC' }}
                >
                    <AutocompleteInput
                        optionText={formatProductChoice}
                        filterToQuery={(searchText: string) => ({ product_search: searchText })}
                        validate={[required()]}
                        fullWidth
                    />
                </ReferenceInput>
            </Grid>

            <Grid item xs={12} sm={4}>
                <NumberInput
                    source="order_quantity"
                    label="Quantity (MT)"
                    validate={[required()]}
                    fullWidth
                    step={0.001}
                    helperText="Decimals allowed, e.g. 1.5 MT"
                />
            </Grid>
            <Grid item xs={12} sm={4}>
                <NumberInput source="order_price" label="Price" validate={[required()]} fullWidth />
            </Grid>
            <Grid item xs={12} sm={4}>
                <NumberInput
                    source="order_gst_rate"
                    label="GST Rate"
                    validate={[required()]}
                    fullWidth
                    helperText="Use 0.18 or 18 for 18%"
                />
            </Grid>
            <Grid item xs={12} sm={4}>
                <SelectInput source="order_discount_type" label="Discount Type" choices={ORDER_DISCOUNT_TYPES} optionText="name" optionValue="id" fullWidth />
            </Grid>
            <Grid item xs={12} sm={4}>
                <FormDataConsumer>
                    {({ formData, ...rest }) => (
                        <NumberInput
                            source="order_discount_value"
                            label={formData.order_discount_type === 'fixed' ? 'Discount per kg' : 'Discount Percent'}
                            fullWidth
                            step={0.01}
                            validate={[required()]}
                            {...rest}
                        />
                    )}
                </FormDataConsumer>
            </Grid>
            <Grid item xs={12} sm={6}>
                <NumberInput source="order_commission" label="Commission" fullWidth step={0.01} helperText="Per kg" />
            </Grid>
            <Grid item xs={12} sm={6}>
                <NumberInput source="order_delivery_charges" label="Delivery Charges" fullWidth step={0.01} />
            </Grid>
            <Grid item xs={12} sm={6}>
                <NumberInput
                    source="order_commercial_credit_note_discount"
                    label="Commercial Credit Note Discount"
                    fullWidth
                    step={0.01}
                    helperText="Per kg"
                />
            </Grid>
            <Grid item xs={12}>
                <CalculatedTotalInput />
            </Grid>

            <Grid item xs={12}>
                <TextInput source="order_tracking_url" label="Tracking URL" fullWidth />
            </Grid>
        </Grid>
    </SimpleForm>
);

export default OrderForm;
